import React, { useState, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, 
  Briefcase, Newspaper, Home, University, Contact
} from 'lucide-react';
import WildlifeFooter from './components/Footer';
import { Helmet } from 'react-helmet';
import ChatBotToggle from './components/ChatBotToggle';
import ThemeToggle from './components/ThemeToggle';

// Lazy load components for performance
const Hero = lazy(() => import('./components/Hero'));
const Blog = lazy(() => import('./components/Blogs'));
const News = lazy(() => import('./components/News'));
const CaseStudies = lazy(() => import('./components/CaseStudies'));
const AdminDashboard = lazy(() => import('./components/admin'));
const CaseStudyDetails = lazy(() => import('./components/CaseStudyDetails'));
const ContactUs = lazy(() => import('./components/Contats'));
const CaseStudiesForm = lazy(() => import('./components/CaseStudiesForm'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Performance-optimized theme and navigation context
const ThemeContext = React.createContext();

function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('theme') === 'dark'
  );

  const toggleTheme = useCallback(() => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      <div className={darkMode ? 'dark' : ''}>{children}</div>
    </ThemeContext.Provider>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { darkMode, toggleTheme } = React.useContext(ThemeContext);

  const navLinks = [
    { to: '/', icon: Home, label: 'Home' },
    { to: '/services', icon: University, label: 'Services' },
    { to: '/blog', icon: Briefcase, label: 'Blog' },
    { to: '/news', icon: Newspaper, label: 'News' },
    { to: '/contact-us', icon: Contact, label: 'Contact Us' }
  ];

  return (
    <Router>
      <ScrollToTop />
      <Helmet>
              {/* Basic SEO Meta Tags */}
              <title>Pixel Meta Lab | Digital Marketing Agency | Colombo</title>
              <meta name="description" content="Pixel Mata Lab, a leading digital advertising firm in Colombo, specializes in cutting-edge marketing solutions, including SEO, PPC, social media marketing, and web development. Elevate your brand with data-driven strategies and innovative digital campaigns. Visit us in Colombo for expert digital advertising services!" />
              
              <meta name="description" content="Pixel Mata Lab, a leading digital advertising firm in Colombo, specializes in cutting-edge marketing solutions, including SEO, PPC, social media marketing, and web development. Elevate your brand with data-driven strategies and innovative digital campaigns. Visit us in Colombo for expert digital advertising services!" />
              <meta name="keywords" content="Digital marketing agency,Best digital marketing company, Online marketing services, Digital marketing expert, Marketing solutions for businesses, Comprehensive marketing strategies , locate in Colombo,digital marketing, Colombo, Sri Lanka, SEO, PPC, social media marketing, web development, Pixel Meta Lab" />
              <meta name="robots" content="index, follow" />
              <link rel="canonical" href="https://pixelmetalab.com/" />
              <meta
                name="keywords"
                content="digital marketing, Colombo, Sri Lanka, SEO, PPC, social media marketing, web development, Pixel Meta Lab"
              />
              
              {/* Open Graph / Facebook */}
              <meta property="og:type" content="https://pixelmetalab.com/" />
              <meta property="og:url" content="https://pixelmetalab.com/" />
              <meta property="og:title" content="Branding and design solutions" />
              <meta property="og:description" content="Transform your business with data-driven digital marketing strategies. We offer innovative solutions for e-commerce, SaaS, and content marketing" />
              <meta property="og:image" content="/images/dark.png" />
      
              {/* Twitter */}
              <meta property="twitter:card" content="summary_large_image" />
              <meta property="twitter:url" content="https://pixelmetalab.com/" />
              <meta property="twitter:title" content="Social media marketing services" />
              <meta property="twitter:description" content="Transform your business with data-driven digital marketing strategies. We offer innovative solutions for e-commerce, SaaS, and content marketing." />
              <meta property="twitter:image" content="/images/twitter-image.jpg" />
            </Helmet>
      <div className={`
        min-h-screen 
        ${darkMode 
          ? 'bg-gradient-to-br from-gray-900 to-black text-white' 
          : 'bg-gradient-to-br from-gray-100 to-white text-gray-900'}
        transition-colors duration-300
      `}>
        <motion.header 
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="sticky top-0 z-50 backdrop-blur-md bg-opacity-70 shadow-lg"
        >
          <div className="container mx-auto flex justify-between items-center p-4">
            <div className="flex items-center">
              <Link to="/">
                <motion.img 
                  whileHover={{ scale: 1.05 }}
                  src="/images/logo.webp" 
                  alt="Light Logo" 
                  className="block rounded-lg bg-transparent dark:hidden w-16 h-16"
                />
              </Link>
              <Link to="/">
                <motion.img
                  whileHover={{ scale: 1.05 }} 
                  src="/images/logo.webp" 
                  alt="Dark Logo" 
                  className="hidden dark:block rounded-lg bg-transparent w-16 h-16"
                />
              </Link>
            </div>

            {/* New Theme Toggle Component */}
            <ThemeToggle darkMode={darkMode} toggleTheme={toggleTheme} />

            {/* Mobile Menu Toggle */}
            <motion.button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="sm:hidden"
              whileTap={{ scale: 0.9 }}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>

            {/* Desktop Navigation */}
            <nav className="hidden sm:flex space-x-4">
              {navLinks.map(({ to, icon: Icon, label }) => (
                <NavLink 
                  key={to} 
                  to={to} 
                  className={({ isActive }) => `
                    flex items-center gap-2 
                    ${isActive 
                      ? 'text-blue-600 font-bold' 
                      : 'hover:text-blue-400'}
                    transition-colors
                  `}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
        </motion.header>

        {/* Mobile Navigation Slide */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="fixed inset-0 z-40 bg-white dark:bg-gray-900 sm:hidden"
            >
              <div className="flex flex-col h-full justify-center items-center space-y-6">
                {navLinks.map(({ to, icon: Icon, label }) => (
                  <motion.div 
                    key={to}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <NavLink 
                      to={to} 
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) => `
                        flex items-center gap-4 text-2xl 
                        ${isActive ? 'text-blue-600' : ''}
                      `}
                    >
                      <Icon size={24} />
                      {label}
                    </NavLink>
                  </motion.div>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>

        {/* Main Content with Transition */}
        <main className="container mx-auto px-4 py-8">
          <Suspense fallback={
            <div className="flex justify-center items-center h-screen">
              Loading...
            </div>
          }>
            <Routes>
              <Route 
                path="/" 
                element={
                  <>
                    <Helmet>
                      <title>Home</title>
                    </Helmet>
                    <Hero />
                  </>
                } 
              />
              <Route 
                path="/services" 
                element={
                  <>
                    <Helmet>
                      <title>Services</title>
                    </Helmet>
                    <CaseStudies />
                  </>
                } 
              />
              <Route 
                path="/services/:id" 
                element={
                  <>
                    <Helmet>
                      <title>Case Study Details</title>
                    </Helmet>
                    <CaseStudyDetails />
                  </>
                } 
              />
              <Route 
                path="/blog" 
                element={
                  <>
                    <Helmet>
                      <title>Blog</title>
                    </Helmet>
                    <Blog />
                  </>
                } 
              />
              <Route 
                path="/news" 
                element={
                  <>
                    <Helmet>
                      <title>News</title>
                    </Helmet>
                    <News />
                  </>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <>
                    <Helmet>
                      <title>Admin</title>
                    </Helmet>
                    <AdminDashboard />
                  </>
                } 
              />
              <Route 
                path="/contact-us" 
                element={
                  <>
                    <Helmet>
                      <title>Contact Us</title>
                    </Helmet>
                    <ContactUs />
                  </>
                } 
              />
              <Route 
                path="/casestudies-form" 
                element={
                  <>
                    <Helmet>
                      <title>CaseStudies Form</title>
                    </Helmet>
                    <CaseStudiesForm/>
                  </>
                } 
              />
            </Routes>
          </Suspense>
        </main>
        <WildlifeFooter/>
        <ChatBotToggle />
      </div>
    </Router>
  );
}

function WrappedApp() {
  return (
    <ThemeProvider>
      <App />
    </ThemeProvider>
  );
}

export default WrappedApp;